import React from 'react';
import ReactDOM from 'react-dom';
import { Lock, Sparkles, X, Check, Crown, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { customToast } from '../../utils/customToast';
import { isPlanOn } from '../../utils/plan'; // w2_07

// Feature names for display
const FEATURE_NAMES = {
  quiz: 'Should I Buy This? Quiz',
  opportunityCost: 'What Am I Giving Up? Quiz',
  csvExport: 'Export to CSV',
  monthlyDonut: 'Monthly Budget Stats',
  yearSummary: 'Yearly Budget Stats',
  yearlyDonut: 'Yearly Budget Stats',
  wishlist: 'Wishlist',
  netWorthTrends: 'Net Worth Trend Chart',
  savingsTrends: 'Sinking Fund & Investment Trend Charts',
  investmentsTrends: 'Sinking Fund & Investment Trend Charts',
  netWorthHistory: 'Net Worth History',
  cards: 'Additional Cards',
  sinkingFunds: 'Additional Sinking Funds',
  investmentAccounts: 'Additional Investment Accounts',
  futureIncome: 'Future Income Planning',
  recurringTransactions: 'Recurring Transactions',
  insights: 'Personalized Insights',
  contributionsPlanner: 'Contributions Planner',
  notesReminders: 'Notes, Reminders & Budgets',
  futureWorth: 'Future Worth Calculator',
};

// Trial Prompt shown after onboarding
export const TrialPrompt = ({ onClose, onPurchaseSuccess }) => {
  const { startTrial, skipTrial, purchaseSubscription, restoreSubscription, premiumPrice, purchasing, theme } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  const [restoring, setRestoring] = React.useState(false);
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  const handleRestorePurchase = async () => {
    setRestoring(true);
    const result = await restoreSubscription();
    setRestoring(false);
    if (result.success) {
      onClose?.();
      onPurchaseSuccess?.();
    } else if (result.notFound) {
      alert('No active subscription found. If you believe this is an error, please contact support.');
    } else {
      alert('Restore failed. Please try again.');
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    const result = await purchaseSubscription();
    setLoading(false);
    if (result.success) {
      onClose?.();
      onPurchaseSuccess?.();
    } else if (!result.cancelled) {
      customToast.error('Purchase failed', 'Please try again.');
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    await skipTrial();
    setLoading(false);
    onClose?.();
  };

  const premiumFeatures = [
    { icon: '📊', text: 'Up to 8 funds and up to 5 investment accounts' },
    { icon: '💳', text: 'Up to 3 debit/credit cards' },
    { icon: '📈', text: 'Sinking funds, investments & net worth trends' },
    { icon: '📅', text: 'Net worth history' },
    { icon: '❓', text: 'Should I Buy This? quiz' },
    { icon: '🎨', text: 'Change your app icon to match your theme' },
    { icon: '💵', text: 'Monthly & yearly budget stats' },
    { icon: '🛍️', text: 'Wishlist to track items you want to purchase' },
    { icon: '✏️', text: 'Future income planning' },
    { icon: '➡️', text: 'Recurring transactions' },
    { icon: '✨', text: 'Personalized insights' },
    { icon: '💰', text: "What Am I Giving Up? calculator" },
    { icon: '🎯', text: 'Canadian investments contributions tracker' },
    { icon: '📝', text: 'Notes, reminders & one-time budget planning' },
    { icon: '🔮', text: 'Future Worth calculator with saved snapshots' },
  ];

  // Theme-based colors
  const getHeaderTint = () => {
    if (isWarm) return 'linear-gradient(135deg, rgba(124, 92, 58, 0.12) 0%, rgba(124, 144, 112, 0.06) 100%)';
    if (isPink) return 'linear-gradient(135deg, rgba(233, 30, 140, 0.10) 0%, rgba(168, 85, 247, 0.05) 100%)';
    return 'linear-gradient(135deg, rgba(76, 59, 207, 0.10) 0%, rgba(56, 184, 200, 0.05) 100%)';
  };

  const getAccentGradient = () => {
    if (isWarm) return 'linear-gradient(90deg, #7C5C3A 0%, #B07D50 50%, #7C9070 100%)';
    if (isPink) return 'linear-gradient(90deg, #FF3CAC 0%, #a855f7 100%)';
    return 'linear-gradient(90deg, #4C3BCF 0%, #6366f1 50%, #38b8c8 100%)';
  };

  const getThemeSolidColor = () => {
    if (isWarm) return '#7C5C3A';
    if (isPink) return '#e879a0';
    return '#4C3BCF';
  };

  const getButtonShadow = () => {
    if (isPlanOn()) return 'none'; // w2_07: black button, no glow
    if (isWarm) return '0 6px 16px -4px rgba(124, 92, 58, 0.4)';
    if (isPink) return '0 6px 16px -4px rgba(233, 30, 140, 0.4)';
    return '0 6px 16px -4px rgba(76, 59, 207, 0.4)';
  };

  // Body scroll lock while modal open
  React.useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center" style={{ zIndex: 9999, paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <div
        className="bg-white w-full flex flex-col"
        style={{
          maxWidth: '480px',
          maxHeight: '92vh',
          borderTopLeftRadius: '28px',
          borderTopRightRadius: '28px',
          borderBottomLeftRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
          borderBottomRightRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
          marginBottom: typeof window !== 'undefined' && window.innerWidth >= 640 ? '40px' : '0',
          boxShadow: '0 -10px 40px -10px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Header (tint extends to top, includes grabber) */}
        <div
          className="px-6 pt-3 pb-5 text-center flex-shrink-0"
          style={{ background: getHeaderTint() }}
        >
          <div className="flex justify-center mb-3">
            <div style={{ width: '36px', height: '5px', background: 'rgba(0,0,0,0.15)', borderRadius: '3px' }} />
          </div>
          <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: '12px', fontFamily: "'DM Sans', sans-serif", background: getAccentGradient(), WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block' }}>
            Try Worthly Premium
          </p>
          <div className="flex justify-center mb-3">
            <img
              src="/logoroundcorners.png"
              alt="Worthly"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                boxShadow: '0 8px 20px -6px rgba(233, 30, 140, 0.35)',
              }}
            />
          </div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', lineHeight: 1.1, color: '#1a1a1a', letterSpacing: '-0.015em', paddingBottom: '4px' }}>
            {isPlanOn() ? 'A clearer way to see your money.' : (
              <>
                A{' '}
                <span style={{ background: getAccentGradient(), WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic', display: 'inline-block', paddingRight: '0.05em' }}>
                  clearer
                </span>
                {' '}way to see your money.
              </>
            )}
          </h2>
          <p style={{ fontSize: '14px', color: '#4a4a4a', marginTop: '16px', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, textAlign: 'left' }}>
            If you've ever felt behind on your finances or overwhelmed by spreadsheets, Worthly is for you!
          </p>
          <p style={{ fontSize: '14px', color: '#4a4a4a', marginTop: '14px', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, textAlign: 'left' }}>
            If you dread opening your banking app, never quite sure what you can spend without guilt or how much you can afford to save, Worthly's premium features will help you finally understand your full financial picture. Try Premium free for 14 days!
          </p>
        </div>

        {/* Features (scrollable) */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#9a9a9a', marginBottom: '10px', fontFamily: "'DM Sans', sans-serif" }}>
            What's included
          </p>
          <div className="grid grid-cols-2 gap-2" style={{ gridAutoRows: '1fr' }}>
            {premiumFeatures.map((feature, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 12px', background: 'rgba(0,0,0,0.03)', borderRadius: '14px' }}>
                <span style={{ fontSize: '17px', flexShrink: 0 }}>{feature.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#3a3a3a', lineHeight: 1.3 }}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer (pinned) */}
        <div
          className="px-5 pt-3 border-t border-gray-100 flex-shrink-0"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 8px) + 8px)' }}
        >
          <div className="text-center mb-2">
            <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', color: '#1a1a1a', lineHeight: 1.1 }}>{premiumPrice || '$4.99'}/month</p>
            <p style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '3px', lineHeight: 1.4 }}>
              14-day free trial, then billed monthly. <a href="https://worthly-app.netlify.app/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#8a8a8a', textDecoration: 'underline' }}>Terms of Use</a>
            </p>
            <p style={{ fontSize: '11.5px', color: '#059669', marginTop: '6px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
              ✓ Cancel anytime before the trial ends, you won't be charged!
            </p>
          </div>
          <Button
            onClick={handleStartTrial}
            disabled={loading || purchasing}
            className="w-full text-white font-semibold border-0"
            style={{ background: getThemeSolidColor(), boxShadow: getButtonShadow(), padding: '14px 24px', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", borderRadius: '16px', WebkitTapHighlightColor: 'transparent' }}
          >
            {loading || purchasing ? 'Processing...' : ('Start 14-Day Free Trial')}
          </Button>
          <button
            onClick={handleSkip}
            disabled={loading}
            className="w-full text-xs py-1.5"
            style={{ color: '#6a6a6a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, marginTop: '4px' }}
          >
            Maybe later, continue with free plan
          </button>
          <button
            onClick={handleRestorePurchase}
            disabled={restoring}
            className="w-full text-xs flex items-center justify-center gap-1"
            style={{ color: 'rgba(0,0,0,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 2px' }}
          >
            <RotateCcw className={`w-3 h-3 ${restoring ? 'animate-spin' : ''}`} />
            {restoring ? 'Restoring...' : 'Restore Purchase'}
          </button>
        </div>
      </div>
    </div>
  );
};

// While a full-screen modal (Settings) is open it shows the paywall itself,
// inside its own layer, so the app-level paywall steps aside.
export const usePaywallHost = (active) => {
  React.useEffect(() => {
    if (!active) return undefined;
    document.body.dataset.worthlyPaywallHost = '1';
    return () => { delete document.body.dataset.worthlyPaywallHost; };
  }, [active]);
};

// Paywall modal for locked features
export const PaywallModal = ({ onPurchaseSuccess, hosted = false }) => {
  const { showPaywall, paywallFeature, dismissPaywall, startTrial, purchaseSubscription, restoreSubscription, purchasing, premiumPrice, subscription, theme } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  const [restoring, setRestoring] = React.useState(false);
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  const handleRestorePurchase = async () => {
    setRestoring(true);
    const result = await restoreSubscription();
    setRestoring(false);
    if (result.success) {
      dismissPaywall();
      onPurchaseSuccess?.();
    } else if (result.notFound) {
      alert('No active subscription found. If you believe this is an error, please contact support.');
    } else {
      alert('Restore failed. Please try again.');
    }
  };

  // Body scroll lock while modal open (must be before early return per React hooks rules)
  React.useEffect(() => {
    if (!showPaywall) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [showPaywall]);

  if (!showPaywall) return null;
  // Only one copy shows: the hosted one while a host is open, else the app's.
  const hostOpen = typeof document !== 'undefined' && !!document.body.dataset.worthlyPaywallHost;
  if (hostOpen !== hosted) return null;

  const featureName = FEATURE_NAMES[paywallFeature] || 'This feature';
  const hasUsedTrial = subscription?.trial_start;

  const handleStartTrial = async () => {
    setLoading(true);
    const result = await purchaseSubscription();
    setLoading(false);
    if (result.success) {
      onPurchaseSuccess?.();
    } else if (!result.cancelled) {
      customToast.error('Purchase failed', 'Please try again.');
    }
  };

  // Theme-based colors
  const getHeaderTint = () => {
    if (isWarm) return 'linear-gradient(135deg, rgba(124, 92, 58, 0.12) 0%, rgba(124, 144, 112, 0.06) 100%)';
    if (isPink) return 'linear-gradient(135deg, rgba(233, 30, 140, 0.10) 0%, rgba(168, 85, 247, 0.05) 100%)';
    return 'linear-gradient(135deg, rgba(76, 59, 207, 0.10) 0%, rgba(56, 184, 200, 0.05) 100%)';
  };


  const getThemeSolidColor = () => {
    if (isWarm) return '#7C5C3A';
    if (isPink) return '#e879a0';
    return '#4C3BCF';
  };

  const getButtonShadow = () => {
    if (isPlanOn()) return 'none'; // w2_07: black button, no glow
    if (isWarm) return '0 6px 16px -4px rgba(124, 92, 58, 0.4)';
    if (isPink) return '0 6px 16px -4px rgba(233, 30, 140, 0.4)';
    return '0 6px 16px -4px rgba(76, 59, 207, 0.4)';
  };

  const getLogoShadow = () => {
    if (isWarm) return '0 8px 20px -6px rgba(124, 92, 58, 0.35)';
    if (isPink) return '0 8px 20px -6px rgba(233, 30, 140, 0.35)';
    return '0 8px 20px -6px rgba(76, 59, 207, 0.35)';
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center"
      data-worthly-paywall="1"
      style={{ zIndex: 9999, paddingTop: "env(safe-area-inset-top, 0px)", pointerEvents: 'auto' }}
      onClick={dismissPaywall}
    >
      <div
        className="bg-white w-full flex flex-col"
        style={{
          maxWidth: '480px',
          maxHeight: '92vh',
          borderTopLeftRadius: '28px',
          borderTopRightRadius: '28px',
          borderBottomLeftRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
          borderBottomRightRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
          marginBottom: typeof window !== 'undefined' && window.innerWidth >= 640 ? '40px' : '0',
          boxShadow: '0 -10px 40px -10px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header (tint extends to top, includes grabber + logo) */}
        <div
          className="px-6 pt-3 pb-5 text-center flex-shrink-0 relative"
          style={{ background: getHeaderTint() }}
        >
          <button
            onClick={dismissPaywall}
            className="absolute top-3 right-3"
            style={{ color: '#8a8a8a', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex justify-center mb-3">
            <div style={{ width: '36px', height: '5px', background: 'rgba(0,0,0,0.15)', borderRadius: '3px' }} />
          </div>
          <div className="flex justify-center mb-3">
            <img
              src="/logoroundcorners.png"
              alt="Worthly"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                boxShadow: getLogoShadow(),
              }}
            />
          </div>
          <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', lineHeight: 1.05, color: '#1a1a1a', letterSpacing: '-0.015em', paddingBottom: '4px' }}>
            Unlock with Premium{/* w2_13 */}
          </h3>
          <p style={{ fontSize: '14px', color: '#4a4a4a', lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif", marginTop: '8px' }}>
            <strong style={{ color: '#1a1a1a', fontWeight: 600 }}>{featureName}</strong> is available with Worthly Premium.
          </p>
        </div>

        {/* Content (scrollable area to keep parity with TrialPrompt structure) */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {hasUsedTrial ? (
            <p style={{ fontSize: '13px', color: '#6a6a6a', lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif", textAlign: 'center' }}>
              Upgrade to Premium to unlock all features.
            </p>
          ) : (
            <p style={{ fontSize: '13px', color: '#6a6a6a', lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif", textAlign: 'center' }}>
              Start your 14-day free trial to unlock {featureName} and every other Premium feature.
            </p>
          )}
        </div>

        {/* Footer (pinned) */}
        <div
          className="px-5 pt-3 border-t border-gray-100 flex-shrink-0"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 8px) + 8px)' }}
        >
          {!hasUsedTrial && (
            <div className="text-center mb-2">
              <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', color: '#1a1a1a', lineHeight: 1.1 }}>{premiumPrice || '$4.99'}/month</p>
              <p style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '3px', lineHeight: 1.4 }}>
                14-day free trial, then billed monthly. <a href="https://worthly-app.netlify.app/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#8a8a8a', textDecoration: 'underline' }}>Terms of Use</a>
              </p>
            </div>
          )}
          {hasUsedTrial ? (
            <Button
              onClick={purchaseSubscription}
              disabled={purchasing}
              className="w-full text-white font-semibold border-0"
              style={{ background: getThemeSolidColor(), boxShadow: getButtonShadow(), padding: '14px 24px', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", borderRadius: '16px', WebkitTapHighlightColor: 'transparent' }}
            >
              {purchasing ? 'Processing...' : (premiumPrice ? `Upgrade - ${premiumPrice}/month` : 'Upgrade to Premium')}
            </Button>
          ) : (
            <Button
              onClick={handleStartTrial}
              disabled={loading || purchasing}
              className="w-full text-white font-semibold border-0"
              style={{ background: getThemeSolidColor(), boxShadow: getButtonShadow(), padding: '14px 24px', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", borderRadius: '16px', WebkitTapHighlightColor: 'transparent' }}
            >
              {loading || purchasing ? 'Processing...' : ('Start 14-Day Free Trial')}
            </Button>
          )}
          <button
            onClick={dismissPaywall}
            className="w-full text-xs py-1.5"
            style={{ color: '#6a6a6a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, marginTop: '4px' }}
          >
            Not now
          </button>
          <button
            onClick={handleRestorePurchase}
            disabled={restoring}
            className="w-full text-xs flex items-center justify-center gap-1"
            style={{ color: 'rgba(0,0,0,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 2px' }}
          >
            <RotateCcw className={`w-3 h-3 ${restoring ? 'animate-spin' : ''}`} />
            {restoring ? 'Restoring...' : 'Restore Purchase'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Wrapper component for premium features with lock overlay
export const PremiumFeature = ({ 
  feature, 
  children, 
  className = '',
  showPreview = true,
  fallback = null 
}) => {
  const { hasAccess, showFeaturePaywall, theme, loading } = useSubscription();
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  const hasFeatureAccess = loading || hasAccess(feature);

  // Lock pill text: the theme's eyebrow colour, same as the white Premium
  // pill on Home's Your Plans header.
  const getLockColor = () => {
    if (isWarm) return '#2D5A3D';
    if (isPink) return '#7B5EA7';
    return '#4361EE';
  };

  // Always render a wrapper div so className (like mb-8) is applied regardless of access
  if (hasFeatureAccess) {
    return <div className={className}>{children}</div>;
  }

  if (!showPreview && fallback) {
    return <div className={className}>{fallback}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Dimmed content */}
      <div className="opacity-30 pointer-events-none select-none blur-[2px]">
        {children}
      </div>
      
      {/* Lock overlay */}
      <div 
        className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm cursor-pointer rounded-xl"
        onClick={() => showFeaturePaywall(feature)}
      >
        <div
          className="rounded-full flex items-center"
          style={{
            background: '#fff',
            color: getLockColor(),
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          {'\uD83D\uDD12'} Premium
        </div>
      </div>
    </div>
  );
};

// Limit indicator component
export const LimitIndicator = ({ resource, current, className = '' }) => {
  const { getLimit, hasPremium, isAdmin, showFeaturePaywall } = useSubscription();
  
  const limit = getLimit(resource);
  const isAtLimit = current >= limit;
  const showUpgrade = isAtLimit && !hasPremium && !isAdmin;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`text-xs ${isAtLimit ? 'text-blue-600' : 'text-gray-400'}`}>
        {current} of {limit}
      </span>
      {showUpgrade && (
        <button 
          onClick={() => showFeaturePaywall(resource)}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          <Lock className="w-3 h-3" />
          Upgrade
        </button>
      )}
    </div>
  );
};

// Premium badge - theme-aware
export const PremiumBadge = ({ className = '' }) => {
  const { theme } = useSubscription();
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  
  const getGradient = () => {
    if (isWarm) return 'linear-gradient(150deg, #7C5C3A 0%, #B07D50 50%, #7C9070 100%)';
    if (isPink) return 'linear-gradient(150deg, #FF3CAC 0%, #a855f7 50%, #6366f1 100%)';
    return 'linear-gradient(150deg, #4C3BCF 0%, #6366f1 50%, #38b8c8 100%)';
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-white text-xs font-medium rounded-full ${className}`}
      style={{ background: getGradient() }}
    >
      <Crown className="w-3 h-3" />
      PRO
    </span>
  );
};

// Subscription Status Card for Settings
export const SubscriptionStatus = () => {
  const { subscription, hasPremium, isAdmin, startTrial, showFeaturePaywall, purchaseSubscription, restoreSubscription, purchasing, premiumPrice, theme } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  const [restoring, setRestoring] = React.useState(false);
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  // Calculate days left in trial
  const getDaysLeft = () => {
    if (!subscription?.trial_end) return 0;
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const daysLeft = getDaysLeft();
  const status = subscription?.status || 'free';

  const handleStartTrial = async () => {
    setLoading(true);
    const result = await purchaseSubscription();
    setLoading(false);
    if (result.success) {
      onPurchaseSuccess?.();
    } else if (!result.cancelled) {
      customToast.error('Purchase failed', 'Please try again.');
    }
  };

  const handleRestorePurchase = async () => {
    setRestoring(true);
    const result = await restoreSubscription();
    setRestoring(false);
    if (result.success) {
      // Subscription restored - UI will update automatically
    } else if (result.notFound) {
      alert('No active subscription found. If you believe this is an error, please contact support.');
    } else {
      alert('Restore failed. Please try again.');
    }
  };

  const buttonClass = isWarm 
    ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' 
    : isPink 
      ? 'bg-pink-500 hover:bg-pink-600'
      : 'bg-indigo-500 hover:bg-indigo-600';

  // Match hero box gradients
  const getGradient = () => {
    if (isWarm) return 'linear-gradient(150deg, #7C5C3A 0%, #B07D50 50%, #7C9070 100%)';
    if (isPink) return 'linear-gradient(150deg, #FF3CAC 0%, #a855f7 50%, #6366f1 100%)';
    return 'linear-gradient(150deg, #4C3BCF 0%, #6366f1 50%, #38b8c8 100%)';
  };

  // Admin user
  if (isAdmin) {
    return (
      <div className="rounded-2xl p-4 text-white relative overflow-hidden" style={{ background: getGradient() }}>
        <div 
          style={{
            position: 'absolute',
            top: '-20px',
            right: '-20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
            pointerEvents: 'none'
          }}
        />
        <div className="flex items-center gap-3 mb-2 relative z-10">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">Admin Account</h3>
            <p className="text-sm text-white/80">Full access to all features</p>
          </div>
        </div>
      </div>
    );
  }

  // Premium user
  if (status === 'premium') {
    return (
      <div className="rounded-2xl p-4 text-white relative overflow-hidden" style={{ background: getGradient() }}>
        <div 
          style={{
            position: 'absolute',
            top: '-20px',
            right: '-20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
            pointerEvents: 'none'
          }}
        />
        <div className="flex items-center gap-3 mb-2 relative z-10">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">Premium Member</h3>
            <p className="text-sm text-white/80">You have access to all features</p>
          </div>
        </div>
      </div>
    );
  }

  // Trial user
  if (status === 'trial' && daysLeft > 0) {
    return (
      <div className="rounded-2xl p-4 text-white relative overflow-hidden" style={{ background: getGradient() }}>
        <div 
          style={{
            position: 'absolute',
            top: '-20px',
            right: '-20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
            pointerEvents: 'none'
          }}
        />
        <div className="flex items-center gap-3 mb-3 relative z-10">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">Free Trial</h3>
            <p className="text-sm text-white/80">{daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining</p>
          </div>
        </div>
        <div className="bg-white/20 rounded-full h-2 mb-2 relative z-10">
          <div 
            className="bg-white rounded-full h-2 transition-all" 
            style={{ width: `${(daysLeft / 14) * 100}%` }}
          />
        </div>
        <p className="text-xs text-white/70 text-center relative z-10">
          Enjoying Premium? Upgrade to keep your features.
        </p>
      </div>
    );
  }

  // Expired trial
  if (status === 'expired') {
    return (
      <div className="bg-gray-100 rounded-2xl p-4 border border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <Lock className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h3 className="font-normal text-gray-700">Trial Expired</h3>
            <p className="text-sm text-gray-500">Upgrade to continue using Premium features</p>
          </div>
        </div>
        <Button
          onClick={purchaseSubscription}
          disabled={purchasing}
          className={`w-full ${buttonClass} text-white mb-2`}
        >
          {purchasing ? 'Processing...' : (premiumPrice ? `Upgrade - ${premiumPrice}/month` : 'Upgrade to Premium')}
        </Button>
        <button
          onClick={handleRestorePurchase}
          disabled={restoring}
          className="w-full text-gray-500 text-sm py-2 hover:text-gray-700 flex items-center justify-center gap-1"
        >
          <RotateCcw className={`w-3 h-3 ${restoring ? 'animate-spin' : ''}`} />
          {restoring ? 'Restoring...' : 'Restore Purchase'}
        </button>
      </div>
    );
  }

  // Free user (never had trial)
  return (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
          <Lock className="w-5 h-5 text-gray-400" />
        </div>
        <div>
          <h3 className="font-normal text-gray-700">Free Plan</h3>
          <p className="text-sm text-gray-500">Limited features available</p>
        </div>
      </div>
      {!hasPremium ? (
        <Button
          onClick={purchaseSubscription}
          disabled={purchasing}
          className={`w-full ${buttonClass} text-white mb-2`}
        >
          {purchasing ? 'Processing...' : ('Start 14-Day Free Trial')}
        </Button>
      ) : null}
      <button
        onClick={handleRestorePurchase}
        disabled={restoring}
        className="w-full text-gray-500 text-sm py-2 hover:text-gray-700 flex items-center justify-center gap-1"
      >
        <RotateCcw className={`w-3 h-3 ${restoring ? 'animate-spin' : ''}`} />
        {restoring ? 'Restoring...' : 'Restore Purchase'}
      </button>
    </div>
  );
};

// Premium Features List Modal
export const PremiumFeaturesModal = ({ open, onClose }) => {
  const { theme, subscription, startTrial, purchaseSubscription, restoreSubscription, purchasing, premiumPrice, hasPremium } = useSubscription();
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  const [restoring, setRestoring] = React.useState(false);

  const handleRestorePurchase = async () => {
    setRestoring(true);
    const result = await restoreSubscription();
    setRestoring(false);
    if (result.success) {
      onClose();
    } else if (result.notFound) {
      alert('No active subscription found. If you believe this is an error, please contact support.');
    } else {
      alert('Restore failed. Please try again.');
    }
  };
  
  const premiumFeatures = [
    { icon: '📊', text: 'Up to 8 funds and up to 5 investment accounts' },
    { icon: '💳', text: 'Up to 3 debit/credit cards' },
    { icon: '📈', text: 'Sinking funds, investments & net worth trends' },
    { icon: '📅', text: 'Net worth history' },
    { icon: '❓', text: 'Should I Buy This? quiz' },
    { icon: '🎨', text: 'Change your app icon to match your theme' },
    { icon: '💵', text: 'Monthly & yearly budget stats' },
    { icon: '🛍️', text: 'Wishlist tracking' },
    { icon: '✏️', text: 'Future income planning' },
    { icon: '➡️', text: 'Recurring transactions' },
    { icon: '✨', text: 'Personalized insights' },
            { icon: '💰', text: "What Am I Giving Up? calculator" },
    { icon: '🎯', text: 'Canadian investments contributions tracker' },
    { icon: '📝', text: 'Notes, reminders & one-time budget planning' },
    { icon: '🔮', text: 'Future Worth calculator' },
  ];

  // Body scroll lock while modal open (must be before early return per React hooks rules)
  React.useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [open]);

  if (!open) return null;

  // Theme-based colors
  const getHeaderTint = () => {
    if (isWarm) return 'linear-gradient(135deg, rgba(124, 92, 58, 0.12) 0%, rgba(124, 144, 112, 0.06) 100%)';
    if (isPink) return 'linear-gradient(135deg, rgba(233, 30, 140, 0.10) 0%, rgba(168, 85, 247, 0.05) 100%)';
    return 'linear-gradient(135deg, rgba(76, 59, 207, 0.10) 0%, rgba(56, 184, 200, 0.05) 100%)';
  };


  const getThemeSolidColor = () => {
    if (isWarm) return '#7C5C3A';
    if (isPink) return '#e879a0';
    return '#4C3BCF';
  };

  const getButtonShadow = () => {
    if (isPlanOn()) return 'none'; // w2_07: black button, no glow
    if (isWarm) return '0 6px 16px -4px rgba(124, 92, 58, 0.4)';
    if (isPink) return '0 6px 16px -4px rgba(233, 30, 140, 0.4)';
    return '0 6px 16px -4px rgba(76, 59, 207, 0.4)';
  };

  const getLogoShadow = () => {
    if (isWarm) return '0 8px 20px -6px rgba(124, 92, 58, 0.35)';
    if (isPink) return '0 8px 20px -6px rgba(233, 30, 140, 0.35)';
    return '0 8px 20px -6px rgba(76, 59, 207, 0.35)';
  };

  const hasStartedTrial = !!subscription?.trial_start;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center animate-in fade-in duration-150"
      style={{ zIndex: 9999, paddingTop: "env(safe-area-inset-top, 0px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full flex flex-col"
        style={{
          maxWidth: '480px',
          maxHeight: '92vh',
          borderTopLeftRadius: '28px',
          borderTopRightRadius: '28px',
          borderBottomLeftRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
          borderBottomRightRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
          marginBottom: typeof window !== 'undefined' && window.innerWidth >= 640 ? '40px' : '0',
          boxShadow: '0 -10px 40px -10px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header (tint extends to top, includes grabber + logo) */}
        <div
          className="px-6 pt-3 pb-5 text-center flex-shrink-0 relative"
          style={{ background: getHeaderTint() }}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3"
            style={{ color: '#8a8a8a', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex justify-center mb-3">
            <div style={{ width: '36px', height: '5px', background: 'rgba(0,0,0,0.15)', borderRadius: '3px' }} />
          </div>
          <div className="flex justify-center mb-3">
            <img
              src="/logoroundcorners.png"
              alt="Worthly"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                boxShadow: getLogoShadow(),
              }}
            />
          </div>
          <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '30px', lineHeight: 1.05, color: '#1a1a1a', letterSpacing: '-0.015em', paddingBottom: '4px' }}>
            Worthly Premium{/* w2_13: one style, one colour */}
          </h3>
          <p style={{ fontSize: '14px', color: '#6a6a6a', marginTop: '6px', fontFamily: "'DM Sans', sans-serif" }}>
            Everything included when you upgrade!
          </p>
        </div>

        {/* Features List - Scrollable */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-2" style={{ gridAutoRows: '1fr' }}>
            {premiumFeatures.map((feature, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 12px', background: 'rgba(0,0,0,0.03)', borderRadius: '14px' }}>
                <span style={{ fontSize: '17px', flexShrink: 0 }}>{feature.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#3a3a3a', lineHeight: 1.3 }}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* PremiumFeaturesModal: bottom padding spacer for premium users
            (no footer renders below for them, so we need explicit space). */}
        {hasPremium && (
          <div
            className="flex-shrink-0"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 8px) + 32px)' }}
          />
        )}

        {/* Footer (pinned) - only show pricing & CTA for non-premium users */}
        {!hasPremium && (
          <div
            className="px-5 pt-3 border-t border-gray-100 flex-shrink-0"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 8px) + 40px)' }}
          >
            <div className="text-center mb-2">
              <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', color: '#1a1a1a', lineHeight: 1.1 }}>{premiumPrice || '$4.99'}/month</p>
              <p style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '3px', lineHeight: 1.4 }}>
                14-day free trial, then billed monthly. <a href="https://worthly-app.netlify.app/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#8a8a8a', textDecoration: 'underline' }}>Terms of Use</a>
              </p>
            </div>
            <Button
              onClick={async () => {
                onClose();
                setTimeout(() => purchaseSubscription(), 300);
              }}
              disabled={purchasing}
              className="w-full text-white font-semibold border-0"
              style={{ background: getThemeSolidColor(), boxShadow: getButtonShadow(), padding: '14px 24px', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", borderRadius: '16px', WebkitTapHighlightColor: 'transparent' }}
            >
              {purchasing ? 'Processing...' : ('Start 14-Day Free Trial')}
            </Button>
            <button
              onClick={handleRestorePurchase}
              disabled={restoring}
              className="w-full text-xs flex items-center justify-center gap-1"
              style={{ color: 'rgba(0,0,0,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 0' }}
            >
              <RotateCcw className={`w-3 h-3 ${restoring ? 'animate-spin' : ''}`} />
              {restoring ? 'Restoring...' : 'Restore Purchase'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumFeature;
